import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Flex,
	Image,
	Spinner,
	Tooltip,
	useDisclosure,
} from '@chakra-ui/react';
import { ChatMessageEnum, IMessage, ThreadRolesEnum } from 'src/lib/schemas';
import { InfoIcon } from '@chakra-ui/icons';
import { toastError } from 'src/services/toast';
import FusionLoading from 'src/components/common/FusionLoading';
import UserLogo from 'src/components/common/UserLogo';
import ThinkingMessage from '../animation/ThinkingMessage';
import Message from './Message';
import Suggestion from './Suggestion';
import ZoomedImage from './ZoomedImage';
import FusionLogo from 'src/components/common/FusionLogo';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

interface IMessages {
	conversation: Array<IMessage>;
	scrollContainerRef?: RefObject<HTMLDivElement>;
}

const Messages: FC<IMessages> = ({ conversation, scrollContainerRef }) => {
	const LOGO_SIZES = {
		h: 8,
		w: 8,
	};
	const { shouldShowTypingIndicator, countryConfig, isLoading, setIsLoading, onboardingState } =
		useRealtimeChatContext();

	const lastItemRef = useRef<HTMLDivElement>(null);
	const wasAtBottomRef = useRef<boolean>(true);
	const previousMessageCountRef = useRef<number>(0);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [retryingMessageId, setRetryingMessageId] = useState<string>();

	// Check if user is scrolled to bottom (with small threshold for rounding errors)
	const isScrolledToBottom = useCallback(() => {
		if (!scrollContainerRef?.current) return true;
		const container = scrollContainerRef.current;
		// Larger threshold for mobile to account for touch scrolling inertia and bounce
		const threshold = 100; // pixels from bottom to consider "at bottom"
		const scrollPosition = container.scrollTop + container.clientHeight;
		const scrollHeight = container.scrollHeight;
		const isAtBottom = scrollHeight - scrollPosition <= threshold;

		// Additional check: if scrollTop is very close to max, definitely at bottom
		const maxScroll = scrollHeight - container.clientHeight;
		const isAtMaxScroll = maxScroll - container.scrollTop <= threshold;

		return isAtBottom || isAtMaxScroll;
	}, [scrollContainerRef]);

	// Track scroll position to know if we should auto-scroll on new messages
	useEffect(() => {
		const container = scrollContainerRef?.current;
		if (!container) return;

		let scrollTimeout: NodeJS.Timeout | null = null;
		let lastScrollTop = container.scrollTop;

		const handleScroll = () => {
			// Debounce scroll events to avoid false positives from inertia scrolling
			if (scrollTimeout) clearTimeout(scrollTimeout);

			scrollTimeout = setTimeout(() => {
				const currentScrollTop = container.scrollTop;
				const isScrollingUp = currentScrollTop < lastScrollTop;
				lastScrollTop = currentScrollTop;

				// Only mark as "not at bottom" if user is intentionally scrolling up
				// Ignore small movements from touch inertia or iOS bounce
				if (isScrollingUp && Math.abs(currentScrollTop - lastScrollTop) > 10) {
					wasAtBottomRef.current = isScrolledToBottom();
				} else {
					// For downward scrolling or small movements, always check if we're at bottom
					wasAtBottomRef.current = isScrolledToBottom();
				}
			}, 50); // 50ms debounce to ignore rapid scroll events (reduced from 100ms for smoother UX)
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			if (scrollTimeout) clearTimeout(scrollTimeout);
			container.removeEventListener('scroll', handleScroll);
		};
	}, [scrollContainerRef, isScrolledToBottom]);

	const handleScrollToBottom = useCallback((instant = false, delay = 0) => {
		if (lastItemRef?.current && scrollContainerRef?.current) {
			// Mark that we're scrolling programmatically
			// This prevents the scroll event listener from thinking the user scrolled up
			wasAtBottomRef.current = true;

			// Use requestAnimationFrame for smoother scrolling during streaming
			// Add optional delay for components that need time to render (e.g., catalog previews)
			const scrollFn = () => {
				requestAnimationFrame(() => {
					if (lastItemRef.current && scrollContainerRef.current) {
						// Try scrollIntoView first
						lastItemRef.current.scrollIntoView({
							behavior: instant ? 'instant' : 'smooth',
							block: 'end',
							inline: 'nearest',
						});

						// For mobile: Also manually set scrollTop to ensure we're at the absolute bottom
						// This helps with cases where scrollIntoView doesn't quite get us there
						requestAnimationFrame(() => {
							if (scrollContainerRef.current) {
								const container = scrollContainerRef.current;
								const maxScroll = container.scrollHeight - container.clientHeight;
								// Only adjust if we're close but not quite at bottom
								if (Math.abs(container.scrollTop - maxScroll) < 50) {
									container.scrollTop = maxScroll;
								}
							}
						});
					}
				});
			};

			if (delay > 0) {
				setTimeout(scrollFn, delay);
			} else {
				scrollFn();
			}
		}
	}, [scrollContainerRef]);

	// Smart auto-scroll: scroll if user was at bottom OR if new message was added
	useEffect(() => {
		const hasNewMessage = conversation.length > previousMessageCountRef.current;
		const isAtBottom = wasAtBottomRef.current;

		// Always scroll when:
		// 1. New message is added AND user was at bottom (to see latest content)
		// 2. Typing indicator appears/disappears (to see assistant writing)
		// Only scroll if user was at bottom to avoid interrupting their reading
		const shouldScroll = (hasNewMessage && isAtBottom) || shouldShowTypingIndicator;

		if (shouldScroll) {
			// Use instant scrolling when assistant is actively writing (streaming content)
			// For widgets/preview components, use smooth scrolling (handled by onContentResize)
			const useInstantScroll = shouldShowTypingIndicator && !hasNewMessage;

			handleScrollToBottom(useInstantScroll);

			// Mark as being at bottom after scrolling
			wasAtBottomRef.current = true;
		}

		// Update the message count tracker
		previousMessageCountRef.current = conversation.length;
	}, [conversation, shouldShowTypingIndicator, handleScrollToBottom]);

	const renderAvatar = useCallback(
		(isUser: boolean) =>
			isUser ? (
				<UserLogo size={LOGO_SIZES} />
			) : (
				<FusionLogo size={LOGO_SIZES} />
			),
		[countryConfig?.avatar],
	);

	const renderAssistantWritting = useMemo(
		() => {
			console.log('[Messages] renderAssistantWritting memoized with currentStep:', onboardingState?.currentStep);
			return (
				<Flex gap={5} alignItems="center">
					{renderAvatar(false)}
					<ThinkingMessage currentStep={onboardingState?.currentStep} />
				</Flex>
			);
		},
		[renderAvatar, onboardingState?.currentStep],
	);

	const handleImageClick = (src: string) => {
		setSelectedImage(src);
		onOpen();
	};

	const ___handleRetryMessage = async (message: IMessage) => {
		try {
			setRetryingMessageId(message.message_id);
			// await ___retryMessage(message);
		} catch (error) {
			toastError(error);
		} finally {
			setRetryingMessageId(undefined);
		}
	};

	const renderSplittedContent = (message: IMessage) => (
		<Flex direction="column" justifyContent="center" gap={2} px={2}>
			{message.splitted_content?.map((content, i) => {
				if (content.type === ChatMessageEnum.Image) {
					return (
						<Box
							key={i}
							borderRadius="md"
							overflow="hidden"
							cursor="pointer"
							onClick={() => handleImageClick(content.value)}
						>
							<Image
								src={content.value}
								alt="User uploaded"
								maxWidth="200px"
								borderRadius="lg"
								objectFit="cover"
							/>
						</Box>
					);
				} else if (content.suggestion) {
					return <Suggestion key={i} content={content.value} />;
				} else {
					return <Message key={i} content={content.value} />;
				}
			})}
		</Flex>
	);

	const renderedMessages = useMemo(() => {
		// Filter out empty messages before rendering (defensive approach)
		const validMessages = conversation.filter((msg) => {
			// Check if message has valid content
			const hasRawMessage = msg.raw_message && msg.raw_message.trim() !== '';
			const hasSplittedContent = msg.splitted_content && msg.splitted_content.length > 0;

			if (!hasRawMessage && !hasSplittedContent) {
				// Only log when actually filtering out empty messages (rare case)
				console.warn('⚠️ [Messages] Filtered empty message:', msg.message_id);
				return false;
			}
			return true;
		});

		return validMessages.map((message, i) => {
			const isLastItem = validMessages.length - 1 === i;
			const isUser = message.role === ThreadRolesEnum.User;

			return (
				<Flex
					key={message.message_id}
					my={3}
					direction={isUser ? 'row-reverse' : 'row'}
				>
					{renderAvatar(isUser)}
					<Box
						bg={'gray.200'}
						borderRadius={20}
						borderTopLeftRadius={isUser ? 20 : 2}
						borderTopRightRadius={isUser ? 2 : 20}
						p={3}
						mt={3}
						ml={isUser ? 0 : 2}
						mr={isUser ? 2 : 0}
						maxW="80%"
						width="auto"
						overflowWrap="break-word"
						wordBreak="break-word"
						whiteSpace="pre-wrap"
					>
						<Flex direction="column" justifyContent="center" gap={3}>
							{message.raw_message ? (
								<Message
									content={message.raw_message}
									onImageClick={handleImageClick}
									onContentResize={
										isLastItem ? () => {
											// Use smooth scroll for widgets, instant only for streaming text
											const hasWidget = message.raw_message?.includes('-preview') ||
											                  message.raw_message?.includes('<catalog') ||
											                  message.raw_message?.includes('<location');
											const useSmooth = hasWidget && !message.streaming;
											handleScrollToBottom(!useSmooth);
										} : undefined
									}
								/>
							) : (
								renderSplittedContent(message)
							)}
						</Flex>
					</Box>
					{message.error && (
						<Box display="inline-block" alignSelf="center" mx={2}>
							{retryingMessageId && retryingMessageId === message.message_id ? (
								<Spinner size="xs" />
							) : (
								<InfoIcon
									color="red.500"
									cursor="pointer"
									onClick={() => ___handleRetryMessage(message)}
								/>
							)}
						</Box>
					)}
				</Flex>
			);
		});
	}, [conversation, retryingMessageId]);

	// Show full-screen loading only on initial load (no messages yet)
	const showFullLoading = isLoading && conversation.length === 0;

	return (
		<>
			<Flex flex={1} direction="column" p={4} overflowX="hidden" pt={0}>
				{showFullLoading && <FusionLoading isLoading={showFullLoading} />}
				<Box visibility={showFullLoading ? 'hidden' : 'visible'}>
					{renderedMessages}
					{shouldShowTypingIndicator && (
						<Flex my={3}>{renderAssistantWritting}</Flex>
					)}
					<div
						ref={lastItemRef}
						style={{ height: '1px', marginBottom: shouldShowTypingIndicator ? '0px' : '20px' }}
					></div>
				</Box>
			</Flex>

			{selectedImage && (
				<ZoomedImage
					isOpen={isOpen}
					onClose={onClose}
					imageSrc={selectedImage}
				/>
			)}
		</>
	);
};

export default Messages;
